import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, CheckCircle, AlertCircle, Building2, User, FileText } from 'lucide-react';
import SimpleReactValidator from 'simple-react-validator';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const RFQModal = ({ isOpen, onClose, items = [] }) => {
    const [, forceUpdate] = useState();
    const validator = useRef(new SimpleReactValidator({
        className: 'jk-rfq-form__error'
    }));

    // Form state
    const [formData, setFormData] = useState({
        // Contact Information
        name: '',
        email: '',
        phone: '',
        // Company Information
        companyName: '',
        companyType: 'commercial',
        // Government Information (conditional)
        contractNumber: '',
        dunsNumber: '',
        cageCode: '',
        // Additional Notes
        notes: ''
    });

    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [rfqReference, setRfqReference] = useState('');

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSubmitStatus(null);
            setRfqReference('');
        }
    }, [isOpen]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Calculate totals
    const estimatedValue = items.reduce((sum, item) => {
        const price = item.priceNumeric || parseFloat(item.price?.replace(/[^0-9.]/g, '')) || 0;
        return sum + (price * item.quantity);
    }, 0);

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validator.current.allValid()) {
            validator.current.showMessages();
            forceUpdate({});
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch(`${API_BASE_URL}/rfq/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    contact: {
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone
                    },
                    company: {
                        name: formData.companyName,
                        type: formData.companyType
                    },
                    government: formData.companyType === 'government' ? {
                        contractNumber: formData.contractNumber,
                        dunsNumber: formData.dunsNumber,
                        cageCode: formData.cageCode
                    } : null,
                    items: items.map(item => ({
                        id: item.id,
                        name: item.name,
                        partNumber: item.partNumber,
                        manufacturer: item.manufacturer,
                        quantity: item.quantity,
                        price: item.price
                    })),
                    notes: formData.notes,
                    estimatedValue
                })
            });

            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                setRfqReference(result.data.referenceNumber);
            } else {
                throw new Error(result.error || 'Failed to submit quote request');
            }
        } catch (error) {
            console.error('RFQ submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="jk-rfq-modal__overlay" onClick={onClose}>
            <div className="jk-rfq-modal" onClick={(e) => e.stopPropagation()}>
                <div className="jk-rfq-modal__header">
                    <h2>
                        <FileText size={24} />
                        Request for Quote
                    </h2>
                    <button className="jk-rfq-modal__close" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                <div className="jk-rfq-modal__content">
                    {/* Success State */}
                    {submitStatus === 'success' && (
                        <div className="jk-rfq-modal__success">
                            <CheckCircle size={64} />
                            <h3>Quote Request Submitted!</h3>
                            <p>Your reference number is:</p>
                            <span className="jk-rfq-modal__reference">{rfqReference}</span>
                            <p>We'll contact you within 1-2 business days with your quote.</p>
                            <button className="jk-btn jk-btn--primary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    )}

                    {/* Error State */}
                    {submitStatus === 'error' && (
                        <div className="jk-rfq-modal__error-state">
                            <AlertCircle size={48} />
                            <h3>Submission Failed</h3>
                            <p>There was an error submitting your quote request. Please try again.</p>
                            <button
                                className="jk-btn jk-btn--secondary"
                                onClick={() => setSubmitStatus(null)}
                            >
                                Try Again
                            </button>
                        </div>
                    )}

                    {/* Form */}
                    {!submitStatus && (
                        <form onSubmit={handleSubmit} className="jk-rfq-form">
                            {/* Items Summary */}
                            <div className="jk-rfq-form__section">
                                <h3 className="jk-rfq-form__section-title">
                                    Items ({items.length})
                                </h3>
                                <div className="jk-rfq-form__items">
                                    {items.map((item, index) => (
                                        <div key={index} className="jk-rfq-form__item">
                                            <div className="jk-rfq-form__item-info">
                                                <span className="jk-rfq-form__item-name">{item.name}</span>
                                                <span className="jk-rfq-form__item-part">
                                                    {item.manufacturer} - {item.partNumber}
                                                </span>
                                            </div>
                                            <span className="jk-rfq-form__item-qty">
                                                Qty: {item.quantity}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="jk-rfq-form__section">
                                <h3 className="jk-rfq-form__section-title">
                                    <User size={18} />
                                    Contact Information
                                </h3>
                                <div className="jk-rfq-form__grid">
                                    <div className="jk-rfq-form__field">
                                        <label htmlFor="name">Full Name *</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="John Doe"
                                        />
                                        {validator.current.message('name', formData.name, 'required')}
                                    </div>
                                    <div className="jk-rfq-form__field">
                                        <label htmlFor="email">Email Address *</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="john@company.com"
                                        />
                                        {validator.current.message('email', formData.email, 'required|email')}
                                    </div>
                                    <div className="jk-rfq-form__field">
                                        <label htmlFor="phone">Phone Number *</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="(555) 555-5555"
                                        />
                                        {validator.current.message('phone', formData.phone, 'required|phone')}
                                    </div>
                                </div>
                            </div>

                            {/* Company Information */}
                            <div className="jk-rfq-form__section">
                                <h3 className="jk-rfq-form__section-title">
                                    <Building2 size={18} />
                                    Company Information
                                </h3>
                                <div className="jk-rfq-form__grid">
                                    <div className="jk-rfq-form__field">
                                        <label htmlFor="companyName">Company Name *</label>
                                        <input
                                            type="text"
                                            id="companyName"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            placeholder="Acme Corporation"
                                        />
                                        {validator.current.message('companyName', formData.companyName, 'required')}
                                    </div>
                                    <div className="jk-rfq-form__field">
                                        <label htmlFor="companyType">Organization Type *</label>
                                        <select
                                            id="companyType"
                                            name="companyType"
                                            value={formData.companyType}
                                            onChange={handleChange}
                                        >
                                            <option value="commercial">Commercial / Enterprise</option>
                                            <option value="government">Government (Federal/State/Local)</option>
                                            <option value="education">Education</option>
                                            <option value="nonprofit">Non-Profit</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Government Information (conditional) */}
                            {formData.companyType === 'government' && (
                                <div className="jk-rfq-form__section">
                                    <h3 className="jk-rfq-form__section-title">
                                        Government Contract Details
                                    </h3>
                                    <div className="jk-rfq-form__grid jk-rfq-form__grid--3">
                                        <div className="jk-rfq-form__field">
                                            <label htmlFor="contractNumber">Contract Number</label>
                                            <input
                                                type="text"
                                                id="contractNumber"
                                                name="contractNumber"
                                                value={formData.contractNumber}
                                                onChange={handleChange}
                                                placeholder="GS-35F-0000X"
                                            />
                                        </div>
                                        <div className="jk-rfq-form__field">
                                            <label htmlFor="dunsNumber">DUNS Number</label>
                                            <input
                                                type="text"
                                                id="dunsNumber"
                                                name="dunsNumber"
                                                value={formData.dunsNumber}
                                                onChange={handleChange}
                                                placeholder="123456789"
                                            />
                                        </div>
                                        <div className="jk-rfq-form__field">
                                            <label htmlFor="cageCode">CAGE Code</label>
                                            <input
                                                type="text"
                                                id="cageCode"
                                                name="cageCode"
                                                value={formData.cageCode}
                                                onChange={handleChange}
                                                placeholder="1ABC2"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Additional Notes */}
                            <div className="jk-rfq-form__section">
                                <h3 className="jk-rfq-form__section-title">
                                    Additional Requirements
                                </h3>
                                <div className="jk-rfq-form__field">
                                    <label htmlFor="notes">Notes (Optional)</label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows={4}
                                        placeholder="Enter any special requirements, delivery preferences, or questions..."
                                    />
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="jk-rfq-form__actions">
                                <button
                                    type="button"
                                    className="jk-btn jk-btn--secondary"
                                    onClick={onClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="jk-btn jk-btn--primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        'Submitting...'
                                    ) : (
                                        <>
                                            <Send size={18} />
                                            Submit Quote Request
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RFQModal;
